import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';

import { i18n, initI18n } from '@/shared/i18n';

interface I18nProviderProps {
  children: ReactNode;
}

type GateState = 'loading' | 'ready' | 'error';

export function I18nProvider({ children }: I18nProviderProps) {
  const [state, setState] = useState<GateState>('loading');

  const load = useCallback(() => {
    setState('loading');
    initI18n()
      .then(() => {
        document.documentElement.lang = i18n.language?.split('-')[0] || 'ko';
        setState('ready');
      })
      .catch(() => setState('error'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Loading gate: hold rendering until the backend-owned label catalog has loaded — text must not render
  // against a missing catalog (it would show raw keys like `staffStatus.ACTIVE`).
  if (state === 'loading') {
    return null;
  }

  // Fetch-failure fallback. The catalog is what failed, so this message is a minimal hardcoded Korean
  // bootstrap string (the one place a literal is allowed — it cannot come from the catalog) + a retry.
  if (state === 'error') {
    return (
      <div
        role="alert"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          color: '#4b5563',
          fontSize: 14,
        }}
      >
        <p>화면 정보를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.</p>
        <button
          type="button"
          onClick={load}
          style={{
            height: 38,
            padding: '0 20px',
            borderRadius: 8,
            border: '1px solid #030213',
            background: '#030213',
            color: '#ffffff',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          다시 시도
        </button>
      </div>
    );
  }

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
